/**
 * The workspace's Sessions tab: this project's chats (yours: a `vfs-<project>` session, or one whose conversation the
 * project records), the sessions of your AI tools on it, and the AI agents of the others working in it right now, on
 * the same kanban as Activity (SessionKanbanBoard). A chat opens its steps; "Continue" goes back to it. A recent chat
 * whose files someone else changed since says so on its card (the drift warning, hooks/useSessionDrift.js).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import SessionKanbanBoard from '../SessionKanbanBoard';
import SessionStepsDrawer from '../SessionStepsDrawer';
import ToolSessionCard, { splitToolSessions, toolSessionKey } from '../sandbox/ToolSessionCard';
import LiveAgentCard from './LiveAgentCard';
import { useAuth } from '../../context/AuthContext';
import useProjectPresence from '../../hooks/useProjectPresence';
import useSessionDrift from '../../hooks/useSessionDrift';
import { driftText } from '../../utils/driftModel';
import {
  cancelAdkSession, deleteAdkSession, listActiveAdkSessions, listAdkSessions, parseVfsSessionId, setSessionArchived, setSessionPinned,
} from '../../services/AdkSessionsService';
import { getMyActivity } from '../../services/SandboxService';
import { findProjectByConversationId } from '../../utils/vfsClient';
import { liveAgentCards, sessionsOfProject, sessionsToLookUp } from '../../utils/sessionsModel';
import { openHistory } from '../../utils/workspaceModel';
import { followPerson } from '../../utils/presenceClient';
import miaFlowAuth from '../../auth/MiaFlowAuth';
import useTabSelectionUrl from '../../hooks/useTabSelectionUrl';
import './Workspace.css';

const ACTIVE_POLL_MS = 5000;
// Which project each chat belongs to, across mounts (a lookup per conversation, at most once per page load).
const lookedUpCache = {};

export default function ProjectSessions({ projectId, focus = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.sub || user?.id || user?.email;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIds, setActiveIds] = useState(() => new Set());
  const [lookedUp, setLookedUp] = useState(() => ({ ...lookedUpCache }));
  const [toolSessions, setToolSessions] = useState([]);
  const [stepsSession, setStepsSession] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const presence = useProjectPresence(projectId);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    const [chats, activity] = await Promise.allSettled([listAdkSessions(userId), getMyActivity({ limit: 50 })]);
    if (!mounted.current) { return; }
    const all = chats.status === 'fulfilled' ? chats.value : [];
    setSessions(all);
    if (activity.status === 'fulfilled') { setToolSessions((activity.value?.sessions || []).filter((s) => s.projectId === projectId)); }
    setLoading(false);
    // The chats whose project only the project's metadata knows: looked up a few at a time.
    const todo = sessionsToLookUp(all, lookedUpCache);
    for (let i = 0; i < todo.length; i += 4) {
      const batch = todo.slice(i, i + 4);
      const found = await Promise.all(batch.map((session) => findProjectByConversationId(session.id).catch(() => null)));
      batch.forEach((session, index) => { lookedUpCache[session.id] = found[index] || null; });
      if (mounted.current) { setLookedUp({ ...lookedUpCache }); }
    }
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load]);

  // The session whose steps are open, in the URL (`?tab=sessions&session=<id>`): a link's opens once the list is read,
  // one that is not there any more is cleaned.
  const [askedSession, setAskedSession] = useState(focus?.session || null);
  useEffect(() => {
    if (loading || !askedSession) { return; }
    const found = sessions.find((s) => s.id === askedSession);
    setAskedSession(null);
    if (found) { setStepsSession(found); }
  }, [loading, sessions, askedSession]);
  useTabSelectionUrl('sessions', { session: stepsSession?.id || null }, { ready: !loading && !askedSession });

  useEffect(() => {
    if (!userId) { return undefined; }
    let cancelled = false;
    const poll = () => listActiveAdkSessions(userId).then((ids) => { if (!cancelled) { setActiveIds(new Set(ids)); } }).catch(() => {});
    poll();
    const timer = setInterval(poll, ACTIVE_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [userId]);

  const mine = useMemo(() => sessionsOfProject(sessions, projectId, lookedUp), [sessions, projectId, lookedUp]);
  // Every chat here is this project's: its changes say which of them drifted.
  const projectOf = useCallback(() => projectId, [projectId]);
  const drifts = useSessionDrift(mine, activeIds, projectOf);
  const cardNotes = useMemo(() => Object.fromEntries(Object.entries(drifts).map(([id, drift]) => [id, (
    <div className="session-drift" data-testid="session-drift" key={id} title={drift.files.join('\n')}>{driftText(drift)}</div>
  )])), [drifts]);

  const openProjectFollowing = (card) => {
    if (card.agent?.driver?.id) { followPerson(projectId, card.agent.driver.id); }
  };

  const extraCards = useMemo(() => {
    const { active, recent } = splitToolSessions(toolSessions);
    const tool = (session) => ({ key: toolSessionKey(session), node: <ToolSessionCard onOpen={() => openHistory({ tab: 'person' })} session={session} /> });
    const others = liveAgentCards(presence).map((card) => ({ key: card.key, node: <LiveAgentCard card={card} onWatch={openProjectFollowing} /> }));
    return { active: [...others, ...active.map(tool)], recent: recent.map(tool) };
  }, [toolSessions, presence]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = async (session, column) => {
    setMovingId(session.id);
    const pinned = column === 'pinned';
    const archived = column === 'archived';
    setSessions((prev) => prev.map((s) => (s.id === session.id ? { ...s, state: { ...(s.state || {}), pinned, archived } } : s)));
    try {
      await setSessionPinned(session.appName, userId, session.id, pinned);
      await setSessionArchived(session.appName, userId, session.id, archived);
    } catch {
      load();
    } finally {
      setMovingId(null);
    }
  };
  const stop = (session) => {
    setActiveIds((prev) => { const next = new Set(prev); next.delete(session.id); return next; });
    cancelAdkSession(session.appName, userId, session.id).catch(() => {});
  };
  const remove = async (ids) => {
    const targets = sessions.filter((s) => ids.includes(s.id));
    setSessions((prev) => prev.filter((s) => !ids.includes(s.id)));
    await Promise.allSettled(targets.map((s) => deleteAdkSession(s.appName, userId, s.id)));
  };
  const continueSession = (session) => {
    const tenant = miaFlowAuth.getSelectedTenant();
    const tenantId = tenant?.tenantId || tenant?.id;
    navigate(tenantId ? `/code/${tenantId}/${projectId}?conversationId=${encodeURIComponent(session.id)}` : `/chat?conversationId=${encodeURIComponent(session.id)}`);
  };

  return (
    <div className="project-sessions" data-testid="project-sessions">
      <p className="project-sessions-hint">
        Chats and AI tool sessions on this project. Others’ agents show while they work: their title and files, never their prompts.
      </p>
      <SessionKanbanBoard
        activeIds={activeIds}
        cardNotes={cardNotes}
        extraCards={extraCards}
        loading={loading}
        movingId={movingId}
        onBulkDelete={remove}
        onCardClick={setStepsSession}
        onMove={move}
        onStop={stop}
        parseSessionId={parseVfsSessionId}
        sessions={mine}
      />
      <SessionStepsDrawer onClose={() => setStepsSession(null)} onOpenSession={continueSession} session={stepsSession} userId={userId} />
    </div>
  );
}

ProjectSessions.propTypes = {
  // `{ session }`: the session whose steps a link opens.
  focus: PropTypes.shape({ session: PropTypes.string }),
  projectId: PropTypes.string.isRequired,
};
